import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { M11_ROLES, hasAnyRole } from '../../lib/roles';
import type { Page, ProcurementAppeal } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, InfoNote, PageHeader, Spinner, StatusBadge } from '../../ui/components';

/** Vendors see + withdraw their own appeals; NPTB Oversight decides everyone's. */
export function AppealsAdmin() {
  const { persona } = useAuth();
  const isOversight = hasAnyRole(persona?.roles, M11_ROLES.NPTB_OVERSIGHT);
  const isAnyStaff = hasAnyRole(persona?.roles, ...Object.values(M11_ROLES));

  return (
    <div>
      <PageHeader title="Procurement Appeals" subtitle="Lodged during a tender's standstill period — an open appeal freezes the standstill clock." />
      {isAnyStaff ? <StaffAppeals canDecide={isOversight} /> : <VendorAppeals />}
    </div>
  );
}

function StaffAppeals({ canDecide }: { canDecide: boolean }) {
  const [status, setStatus] = useState('');
  const query = new URLSearchParams({ limit: '50' });
  if (status) query.set('status', status);
  const listQ = useApiQuery<Page<ProcurementAppeal>>(`/api/v1/m11/appeals?${query.toString()}`);

  return (
    <div>
      <Field label="Status" className="mb-4 max-w-xs">
        <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="OPEN">Open</option>
          <option value="UPHELD">Upheld</option>
          <option value="DISMISSED">Dismissed</option>
          <option value="WITHDRAWN">Withdrawn</option>
        </select>
      </Field>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      {listQ.loading ? <Spinner /> : (listQ.data?.items.length ?? 0) === 0 ? <EmptyState title="No appeals" /> : (
        <div className="space-y-3">
          {listQ.data!.items.map((a) => <AppealCard key={a.id} appeal={a} canDecide={canDecide} onChanged={listQ.reload} />)}
        </div>
      )}
    </div>
  );
}

function VendorAppeals() {
  const { persona } = useAuth();
  const actingFor = persona?.actingForBusinessId;
  // Hooks called unconditionally — see BidTab.tsx's VendorBidPanel for why.
  const mineQ = useApiQuery<{ items: ProcurementAppeal[] }>(actingFor ? '/api/v1/m11/appeals/me' : null, actingFor);

  if (!persona) return <InfoNote message="Sign in as a business representative to view your appeals." />;
  if (!actingFor) return <InfoNote message="Appeals are lodged by the bidding business — sign in as a business representative persona." />;
  return (
    <div>
      {mineQ.error ? <ErrorNote message={mineQ.error} /> : null}
      {mineQ.loading ? <Spinner /> : (mineQ.data?.items.length ?? 0) === 0 ? (
        <EmptyState title="No appeals lodged" hint="Lodge one from a tender's Award page during its standstill period." />
      ) : (
        <div className="space-y-3">
          {mineQ.data!.items.map((a) => <AppealCard key={a.id} appeal={a} canDecide={false} onChanged={mineQ.reload} allowWithdraw />)}
        </div>
      )}
    </div>
  );
}

function AppealCard({ appeal, canDecide, onChanged, allowWithdraw }: { appeal: ProcurementAppeal; canDecide: boolean; onChanged: () => void; allowWithdraw?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [deciding, setDeciding] = useState(false);

  const decide = async (outcome: 'UPHELD' | 'DISMISSED') => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m11/appeals/${appeal.id}/decide`, { outcome, notes });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Decision failed');
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m11/appeals/${appeal.id}/withdraw`);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Withdraw failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between">
        <Link to={`/procurement/awards/${appeal.awardId}`} className="font-mono text-sm font-semibold text-brand-600 hover:underline">{appeal.refNo}</Link>
        <StatusBadge status={appeal.status} />
      </div>
      <p className="mt-1 text-sm text-slate-700">{appeal.grounds}</p>
      <div className="mt-1 text-[11px] text-slate-400">Lodged {formatDateTime(appeal.lodgedAt)}</div>
      {appeal.decisionNotes ? <div className="mt-1 text-xs text-slate-600">Decision: {appeal.decisionNotes}</div> : null}
      {error ? <div className="mt-2"><ErrorNote message={error} /></div> : null}
      {appeal.status === 'OPEN' ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {allowWithdraw ? <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={withdraw} disabled={busy}>Withdraw</button> : null}
          {canDecide ? (
            deciding ? (
              <div className="flex w-full gap-2">
                <input className="field-input" placeholder="Decision notes…" value={notes} onChange={(e) => setNotes(e.target.value)} />
                <button className="btn-success shrink-0 !py-1 text-xs" onClick={() => decide('UPHELD')} disabled={busy || notes.length < 3}>Uphold</button>
                <button className="btn-danger shrink-0 !py-1 text-xs" onClick={() => decide('DISMISSED')} disabled={busy || notes.length < 3}>Dismiss</button>
              </div>
            ) : (
              <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setDeciding(true)}>Decide</button>
            )
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
